import React, { useState, useEffect, useContext } from 'react'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Alert from 'react-bootstrap/Alert'
import { useTableColumns } from 'api/hooks/use-table-columns'
import { useQuery } from 'react-query'
import customColumnService from 'services/customColumnService'
import AuthContext from 'context/auth-context'
import UserContext from 'context/user-context'
import { apiGet } from 'api'
import { titleize } from 'helpers/utils'

function CreateTemplateModal({ show, onHide, dashboard, onSubmit, isLoading }) {
  const token = useContext(AuthContext)
  const { municipality_id } = useContext(UserContext)
  const [templateName, setTemplateName] = useState('')
  const [description, setDescription] = useState('')
  const [displayOrder, setDisplayOrder] = useState(0)
  const [errors, setErrors] = useState({})
  const [accountCodesCache, setAccountCodesCache] = useState({})

  // Date option label mapping
  const dateOptionLabels = {
    // Comparison
    any: 'Any Date',
    less: 'Before',
    greater: 'After',
    between: 'Between',
    // Relative
    Yesterday: 'Yesterday',
    'Past 7 days': 'Past 7 days',
    30: 'Past 30 days',
    60: 'Past 60 days',
    90: 'Past 90 days',
    120: 'Past 120 days',
    'This Month': 'This Month',
    'Last Month': 'Last Month',
    '1 year': 'More than 1 year ago',
    '2 years': 'More than 2 years ago',
    '3 years': 'More than 3 years ago',
    // Quarters
    Q1: 'Q1',
    Q2: 'Q2',
    Q3: 'Q3',
    Q4: 'Q4',
    // Fiscal Year
    PFY: 'Previous Fiscal Year',
    FY: 'Current Fiscal Year'
  }

  // Get table columns for the dashboard's section
  const { data: tableColumns = [] } = useTableColumns({
    params: { section_id: dashboard?.section_id },
    options: { enabled: !!dashboard?.section_id }
  })

  // Get custom columns for the dashboard
  const { data: customColumns = [] } = useQuery(
    ['customColumns', dashboard?.id],
    () => customColumnService.getCustomColumns(token, dashboard?.id),
    {
      enabled: !!dashboard?.id && !!token
    }
  )

  // Filter to only show filterable columns (dashboard/table columns)
  const dashboardFilters = tableColumns
    .filter((col) => {
      // Include columns with a filter_type
      if (col.filter_type && col.filter_type !== '') return true

      // Also include special vendor columns that don't have filter_type but use VendorNameFilterableHeader
      if (
        col.database_column_name === 'vendor_name' ||
        col.database_column_name === 'vendor_id'
      ) {
        return true
      }

      return false
    })
    .map((col) => ({
      id: col.id,
      key: col.accessor_key,
      label: col.name,
      // Treat vendor columns as 'reference' type if they don't have a filter_type
      type:
        col.filter_type ||
        (col.database_column_name === 'vendor_name' ||
        col.database_column_name === 'vendor_id'
          ? 'reference'
          : ''),
      database_column_name: col.database_column_name,
      column_category: 'dashboard'
    }))

  // Add custom columns as available filters
  const customColumnFilters = customColumns.map((cc) => ({
    id: `custom_column_${cc.id}`,
    key: `custom_column_${cc.id}`,
    label: cc.name,
    type: cc.column_type === 'number' ? 'min_max' : 'basic',
    database_column_name: `custom_column_${cc.id}`,
    column_category: 'custom',
    custom_column_id: cc.id,
    custom_column_type: cc.column_type
  }))

  // Define account search columns (using snake_case keys to match dashboard.account_search structure)
  const accountSearchFilters = [
    {
      id: 'orgs',
      key: 'orgs',
      label: 'Organizations',
      type: 'multi_select',
      column_category: 'account'
    },
    {
      id: 'objects',
      key: 'objects',
      label: 'Objects',
      type: 'multi_select',
      column_category: 'account'
    },
    {
      id: 'projects',
      key: 'projects',
      label: 'Projects',
      type: 'multi_select',
      column_category: 'account'
    },
    {
      id: 'seg_ones',
      key: 'seg_ones',
      label: 'Segment 1',
      type: 'multi_select',
      column_category: 'account'
    },
    {
      id: 'seg_twos',
      key: 'seg_twos',
      label: 'Segment 2',
      type: 'multi_select',
      column_category: 'account'
    },
    {
      id: 'seg_threes',
      key: 'seg_threes',
      label: 'Segment 3',
      type: 'multi_select',
      column_category: 'account'
    },
    {
      id: 'seg_fours',
      key: 'seg_fours',
      label: 'Segment 4',
      type: 'multi_select',
      column_category: 'account'
    },
    {
      id: 'seg_fives',
      key: 'seg_fives',
      label: 'Segment 5',
      type: 'multi_select',
      column_category: 'account'
    },
    {
      id: 'seg_sixes',
      key: 'seg_sixes',
      label: 'Segment 6',
      type: 'multi_select',
      column_category: 'account'
    },
    {
      id: 'seg_sevens',
      key: 'seg_sevens',
      label: 'Segment 7',
      type: 'multi_select',
      column_category: 'account'
    },
    {
      id: 'seg_eights',
      key: 'seg_eights',
      label: 'Segment 8',
      type: 'multi_select',
      column_category: 'account'
    }
  ]

  // Combine all available filters
  const availableFilters = [
    ...dashboardFilters,
    ...customColumnFilters,
    ...accountSearchFilters
  ]

  // Fetch account code details for selected IDs when modal opens
  useEffect(() => {
    if (!show || !dashboard?.account_search) return

    const fetchAccountCodes = async () => {
      // Map snake_case keys to camelCase for API
      const codeTypeMapping = {
        orgs: 'orgs',
        objects: 'objects',
        projects: 'projects',
        seg_ones: 'segOnes',
        seg_twos: 'segTwos',
        seg_threes: 'segThrees',
        seg_fours: 'segFours',
        seg_fives: 'segFives',
        seg_sixes: 'segSixes',
        seg_sevens: 'segSevens',
        seg_eights: 'segEights'
      }

      const newCache = {}

      for (const [snakeCaseKey, camelCaseKey] of Object.entries(
        codeTypeMapping
      )) {
        const selectedIds = dashboard.account_search[snakeCaseKey]
        if (selectedIds && selectedIds.length > 0) {
          try {
            // Fetch account codes for this type with the selected IDs
            // The API expects the IDs as a comma-separated string in a parameter matching the code_type
            const response = await apiGet({
              url: `municipalities/${municipality_id}/account_codes`,
              params: {
                code_type: camelCaseKey,
                [snakeCaseKey]: selectedIds.join(','),
                perPage: selectedIds.length + 10 // Ensure we get all selected items
              },
              token
            })

            // Store in cache by snake_case key to match dashboard.account_search structure
            newCache[snakeCaseKey] = response.data || response
          } catch (error) {
            console.error(
              `Error fetching account codes for ${snakeCaseKey}:`,
              error
            )
            newCache[snakeCaseKey] = []
          }
        }
      }

      setAccountCodesCache(newCache)
    }

    fetchAccountCodes()
  }, [show, dashboard?.account_search, municipality_id, token])

  // Helper function to check if a filter has an active value
  const hasActiveFilterValue = (col) => {
    // Handle account search columns
    if (col.column_category === 'account') {
      const accountSearchData = dashboard?.account_search?.[col.key] || []
      return accountSearchData && accountSearchData.length > 0
    }

    // Handle custom columns
    if (col.column_category === 'custom') {
      if (!dashboard?.params) return false

      const baseKey = `custom_column_${col.custom_column_id}`

      // For number custom columns, check min/max keys
      if (col.custom_column_type === 'number') {
        const minKey = `customColumnMin${col.custom_column_id}`
        const maxKey = `customColumnMax${col.custom_column_id}`
        const minValue = dashboard.params[minKey]
        const maxValue = dashboard.params[maxKey]
        return (minValue && minValue !== '') || (maxValue && maxValue !== '')
      }

      // For text custom columns, check the base key
      const value = dashboard.params[baseKey]
      return value && value !== ''
    }

    // Handle dashboard columns
    if (!dashboard?.params) return false

    // Check for special vendor filters (these use vendors/excludedVendors arrays)
    // Only the vendor_name column uses the vendors array, not other vendor-related columns
    if (col.database_column_name === 'vendor_name') {
      const hasVendors = dashboard?.vendors && dashboard?.vendors.length > 0
      const hasExcludedVendors =
        dashboard?.excluded_vendors && dashboard?.excluded_vendors.length > 0
      if (hasVendors || hasExcludedVendors) {
        return true
      }
    }

    // Check for special employee filters (these use employees/excludedEmployees arrays)
    // Employee filters are matched by database_column_name on each employee object
    if (col.database_column_name) {
      const hasEmployees =
        dashboard?.employees &&
        dashboard?.employees.some(
          (emp) => emp.database_column_name === col.database_column_name
        )
      const hasExcludedEmployees =
        dashboard?.excluded_employees &&
        dashboard?.excluded_employees.some(
          (emp) => emp.database_column_name === col.database_column_name
        )
      if (hasEmployees || hasExcludedEmployees) {
        return true
      }
    }

    // For min_max, date, and min_max_percent filter types, check for min/max keys
    if (['min_max', 'date', 'min_max_percent'].includes(col.type)) {
      // Find the original table column to get section_abbreviation
      const tableColumn = tableColumns.find((tc) => tc.id === col.id)
      const prefix = tableColumn?.section_abbreviation?.toLowerCase() || ''
      const suffix = col.key.slice(prefix.length)
      const minKey = `${prefix}Min${suffix}`
      const maxKey = `${prefix}Max${suffix}`
      const minValue = dashboard.params[minKey]
      const maxValue = dashboard.params[maxKey]

      // For date filters, also check option value
      if (col.type === 'date') {
        const optionKey = `${col.key}Option`
        const optionValue = dashboard.params[optionKey]
        if (optionValue && optionValue !== '' && optionValue !== 'any') {
          return true
        }
      }

      return (minValue && minValue !== '') || (maxValue && maxValue !== '')
    }

    // For other filter types, use key directly
    const value = dashboard.params[col.key]
    return (
      value && value !== '' && !(Array.isArray(value) && value.length === 0)
    )
  }

  const handleSubmit = () => {
    const newErrors = {}

    if (!templateName.trim()) {
      newErrors.name = 'Template name is required'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    const templateData = {
      name: templateName,
      description: description,
      display_order: displayOrder
    }

    onSubmit(templateData)
  }

  const handleClose = () => {
    setTemplateName('')
    setDescription('')
    setDisplayOrder(0)
    setErrors({})
    onHide()
  }

  // Helper function to render filter value display
  const renderFilterValue = (filter) => {
    // Handle account search columns
    if (filter.column_category === 'account') {
      const accountSearchIds = dashboard?.account_search?.[filter.key] || []
      if (!accountSearchIds || accountSearchIds.length === 0) return null

      // Get the full account code objects from cache
      const accountCodesForType = accountCodesCache[filter.key] || []

      // Display friendly values from account search items
      const displayValues = accountSearchIds
        .map((id) => {
          // Find the full account code object from cache
          const accountCode = accountCodesForType.find((ac) => ac.id === id)

          if (accountCode) {
            // Format as "Name (Code)" - prefer name with code, or fall back to what's available
            if (accountCode.name && accountCode.code) {
              return `${accountCode.name} (${accountCode.code})`
            } else if (accountCode.name && accountCode.external_id) {
              return `${accountCode.name} (${accountCode.external_id})`
            } else if (accountCode.name) {
              return accountCode.name
            } else if (accountCode.code) {
              return accountCode.code
            } else if (accountCode.external_id) {
              return accountCode.external_id
            }
          }

          // Fallback to showing the ID if we don't have the full object yet
          return id
        })
        .join(', ')

      return (
        <div key={filter.id} className="mb-2">
          <strong>{filter.label}:</strong> {displayValues}
        </div>
      )
    }

    // Handle custom columns
    if (filter.column_category === 'custom') {
      if (!dashboard?.params) return null

      const baseKey = `custom_column_${filter.custom_column_id}`

      // For number custom columns, check min/max keys
      if (filter.custom_column_type === 'number') {
        const minKey = `customColumnMin${filter.custom_column_id}`
        const maxKey = `customColumnMax${filter.custom_column_id}`
        const minValue = dashboard.params[minKey]
        const maxValue = dashboard.params[maxKey]

        if (!minValue && !maxValue) return null

        let displayValue = ''
        if (minValue && minValue !== '') {
          displayValue += `Min: ${minValue}`
        }
        if (maxValue && maxValue !== '') {
          if (displayValue) displayValue += ', '
          displayValue += `Max: ${maxValue}`
        }

        return (
          <div key={filter.id} className="mb-2">
            <strong>{filter.label}:</strong> {displayValue}
          </div>
        )
      }

      // For text custom columns
      const value = dashboard.params[baseKey]
      if (!value || value === '') return null

      let displayValue = value
      const operatorKey = `${baseKey}Operator`
      const operatorValue = dashboard.params[operatorKey]
      if (operatorValue && operatorValue !== '') {
        displayValue += ` (${operatorValue})`
      }

      return (
        <div key={filter.id} className="mb-2">
          <strong>{filter.label}:</strong> {displayValue}
        </div>
      )
    }

    // Handle dashboard columns
    if (!dashboard?.params) return null

    // For min_max, date, and min_max_percent filter types
    if (['min_max', 'date', 'min_max_percent'].includes(filter.type)) {
      const tableColumn = tableColumns.find((tc) => tc.id === filter.id)
      if (!tableColumn) return null

      const prefix = tableColumn.section_abbreviation?.toLowerCase() || ''
      const suffix = filter.key.slice(prefix.length)
      const minKey = `${prefix}Min${suffix}`
      const maxKey = `${prefix}Max${suffix}`
      const minValue = dashboard.params[minKey]
      const maxValue = dashboard.params[maxKey]

      let optionValue = null
      if (filter.type === 'date') {
        const optionKey = `${filter.key}Option`
        optionValue = dashboard.params[optionKey]
      }

      const hasMinMax =
        (minValue && minValue !== '') || (maxValue && maxValue !== '')
      const hasOption =
        optionValue && optionValue !== '' && optionValue !== 'any'

      if (!hasMinMax && !hasOption) return null

      let displayValue = ''
      if (filter.type === 'date' && hasOption) {
        displayValue = dateOptionLabels[optionValue] || optionValue
      }

      if (minValue && minValue !== '') {
        if (displayValue) displayValue += ' - '
        displayValue += `Min: ${minValue}`
      }
      if (maxValue && maxValue !== '') {
        if (displayValue) displayValue += ', '
        displayValue += `Max: ${maxValue}`
      }

      return (
        <div key={filter.id} className="mb-2">
          <strong>{filter.label}:</strong> {displayValue}
        </div>
      )
    }

    // For other filter types
    const value = dashboard.params[filter.key]
    if (
      !value ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return null
    }

    let displayValue = value
    if (Array.isArray(value)) {
      displayValue = value.join(', ')
    } else if (typeof value === 'object') {
      displayValue = JSON.stringify(value)
    }

    if (filter.type === 'basic') {
      const operatorKey = `${filter.key}Operator`
      const operatorValue = dashboard.params[operatorKey]
      if (operatorValue && operatorValue !== '') {
        displayValue += ` (${operatorValue})`
      }
    }

    return (
      <div key={filter.id} className="mb-2">
        <strong>{filter.label}:</strong> {displayValue}
      </div>
    )
  }

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create Dashboard Template</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert variant="info" className="mb-4">
          <small>
            This will create a system-wide template based on the current
            dashboard configuration. Users will be able to create new dashboards
            from this template.
          </small>
        </Alert>

        <Form.Group className="mb-3">
          <Form.Label>
            Template Name <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            placeholder="e.g., AP Vendor Analysis"
            value={templateName}
            onChange={(e) => {
              setTemplateName(e.target.value)
              if (errors.name) {
                setErrors({ ...errors, name: undefined })
              }
            }}
            isInvalid={!!errors.name}
          />
          <Form.Control.Feedback type="invalid">
            {errors.name}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Describe what this template is for..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Form.Text className="text-muted">
            This will help users understand when to use this template
          </Form.Text>
        </Form.Group>

        <div className="mb-3">
          <Form.Label>Current Filter Values</Form.Label>
          <Form.Text className="d-block mb-2 text-muted">
            These filter values are currently applied and will be saved in the
            template:
          </Form.Text>
          <div
            className="border rounded p-3 mb-3 bg-light"
            style={{ maxHeight: '200px', overflowY: 'auto' }}
          >
            <div className="small">
              {/* Render all filter values using availableFilters */}
              {availableFilters.map((filter) => renderFilterValue(filter))}

              {/* Show special filters that don't fit into the availableFilters structure */}
              {dashboard?.vendors && dashboard.vendors.length > 0 && (
                <div className="mb-2">
                  <strong>Vendors:</strong>{' '}
                  {dashboard.vendors.map((v) => v.name).join(', ')}
                </div>
              )}
              {dashboard?.excluded_vendors &&
                dashboard.excluded_vendors.length > 0 && (
                  <div className="mb-2">
                    <strong>Excluded Vendors:</strong>{' '}
                    {dashboard.excluded_vendors.map((v) => v.name).join(', ')}
                  </div>
                )}
              {dashboard?.employees && dashboard.employees.length > 0 && (
                <div className="mb-2">
                  <strong>Employees:</strong>{' '}
                  {dashboard.employees
                    .map((e) => e.name || e.external_id)
                    .join(', ')}
                </div>
              )}
              {dashboard?.excluded_employees &&
                dashboard.excluded_employees.length > 0 && (
                  <div className="mb-2">
                    <strong>Excluded Employees:</strong>{' '}
                    {dashboard.excluded_employees
                      .map((e) => e.name || e.external_id)
                      .join(', ')}
                  </div>
                )}
              {dashboard?.analytics && dashboard.analytics.length > 0 && (
                <div className="mb-2">
                  <strong>Analytics:</strong>{' '}
                  {dashboard.analytics.map((a) => `${titleize(a.options)}: ${a.name}`).join(', ')}
                </div>
              )}
              {dashboard?.minimum_amount && (
                <div className="mb-2">
                  <strong>Minimum Amount:</strong> ${dashboard.minimum_amount}
                </div>
              )}
              {dashboard?.maximum_amount && (
                <div className="mb-2">
                  <strong>Maximum Amount:</strong> ${dashboard.maximum_amount}
                </div>
              )}

              {/* Check if no filters applied */}
              {!availableFilters.some((filter) =>
                hasActiveFilterValue(filter)
              ) &&
                (!dashboard?.vendors || dashboard.vendors.length === 0) &&
                (!dashboard?.excluded_vendors ||
                  dashboard.excluded_vendors.length === 0) &&
                (!dashboard?.employees || dashboard.employees.length === 0) &&
                (!dashboard?.excluded_employees ||
                  dashboard.excluded_employees.length === 0) &&
                (!dashboard?.analytics || dashboard.analytics.length === 0) &&
                !dashboard?.minimum_amount &&
                !dashboard?.maximum_amount && (
                  <div className="text-muted text-center">
                    <small>No filters currently applied</small>
                  </div>
                )}
            </div>
          </div>
        </div>

        <Alert variant="warning" className="mb-0">
          <small>
            <strong>Note:</strong> The template will capture the current
            dashboard's columns, charts, custom columns, and all filter
            settings. When users create a dashboard from this template, the
            filter values will be applied as static defaults.
          </small>
        </Alert>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="light-primary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? 'Creating Template...' : 'Create Template'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default CreateTemplateModal
